import { useEffect, useRef } from "react";
import { I18n } from "react-redux-i18n";

export interface UsePageTitleOptions {
  separator?: string;
  restoreOnUnmount?: boolean;
}

/**
 * Custom hook to set the browser tab title
 * @param title - The title to set
 * @param options - Optional configuration
 */
export const usePageTitle = (
  title: string,
  options: UsePageTitleOptions = {}
): void => {
  const { separator = "|", restoreOnUnmount = true } = options;
  const originalTitle = useRef<string | null>(null);

  useEffect(() => {
    // Check if title is provided and not undefined
    if (!title || typeof title !== "string") {
      return;
    }

    // Store original title on first mount
    if (originalTitle.current === null) {
      originalTitle.current = document.title;
    }

    // Format the title
    const formattedTitle = `${title} ${separator} ${originalTitle.current}`.trim();
    document.title = formattedTitle;

    // Cleanup function to restore original title if requested
    return () => {
      if (restoreOnUnmount && originalTitle.current) {
        document.title = originalTitle.current;
      }
    };
  }, [title, separator, restoreOnUnmount]);
};

export default usePageTitle;
